/**
 * Game Summary — Fanatic variant (FR-SUMMARY-01 Fanatic scope, AC-FANATIC-004,
 * AC-FANATIC-005/006).
 *
 * Two differences from the coach page, both deliberate:
 *
 *  1. **Team aggregates only.** `StatBlock` is rendered with
 *     `isSummaryOnly`, which drops every per-attempt list and per-player
 *     breakdown; the server-side projection already withholds player rows, so
 *     this is defence in depth rather than the only guard.
 *  2. **The video slot disappears rather than apologising.** On a team whose
 *     provider is not fan-visible (Hudl) the embed element is not rendered at
 *     all — `VideoEmbed isHiddenWhenUnavailable` collapses it. Live Video, by
 *     contrast, keeps a visible fail-soft card.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { VideoEmbed } from '../../components/video-embed';
import type { GameSummaryFeed } from '../../lib/api-types';
import { FANATIC_VIEWS } from '../../lib/constants';
import { ScoreByQuarter, ScoringSummary, DEFAULT_DRIVE_FILTERS } from '../summary/scoring-summary';
import type { DriveFilters } from '../summary/scoring-summary';
import { StatBlock } from '../summary/stat-block';

import { toFanaticMessage, useFanaticPortal } from './fanatic-portal';

/** Poll cadence for the spectator view; coaches get the live socket. */
const REFRESH_MS = 15_000;

export function FanaticGameSummaryPage(): JSX.Element {
  const apiClient = useApiClient();
  const { activeGrant, isLoading: isPortalLoading, tenantId, withTeam } = useFanaticPortal();
  const { activeEvent, activeEventId } = useActiveEvent();
  const [feed, setFeed] = useState<GameSummaryFeed | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DriveFilters>(DEFAULT_DRIVE_FILTERS);
  const [reloadToken, setReloadToken] = useState(0);
  const hasLoadedRef = useRef(false);

  const teamName = activeGrant?.team_name ?? 'Your team';

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (activeEventId === null || tenantId === null) {
      setFeed(null);
      return undefined;
    }
    const controller = new AbortController();
    // Only the first paint shows a skeleton; a refresh must not blank the sheet.
    // A ref (not `feed`) keeps this out of the dependency list, which would
    // otherwise re-trigger the effect on every successful load.
    if (!hasLoadedRef.current) setIsLoading(true);
    void (async () => {
      try {
        const loaded = await apiClient.getGameSummary(activeEventId, {
          unit: 'all',
          signal: controller.signal,
        });
        setFeed(loaded);
        hasLoadedRef.current = true;
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load the summary for this game.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [activeEventId, apiClient, reloadToken, tenantId]);

  // Spectator refresh loop. Cleared on unmount so a test never leaks a timer.
  useEffect(() => {
    if (activeEventId === null) return undefined;
    const timer = setInterval(reload, REFRESH_MS);
    return () => clearInterval(timer);
  }, [activeEventId, reload]);

  if (isPortalLoading) return <p className="fan-empty">Loading…</p>;

  if (activeGrant === null) {
    return (
      <section className="fan-panel">
        <h2 className="fan-panel__title">Follow a team first</h2>
        <p className="fan-panel__hint">
          Game summaries are scoped to a team you follow. Pick one from the switcher, or find a new
          team to follow.
        </p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={withTeam(FANATIC_VIEWS.browse)}>
            Find teams
          </Link>
        </div>
      </section>
    );
  }

  if (activeEventId === null) {
    return (
      <section className="fan-panel">
        <h2 className="fan-panel__title">Choose a game</h2>
        <p className="fan-panel__hint">
          Pick one of {teamName}’s games and its score, drives and team totals show up here.
        </p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={withTeam(FANATIC_VIEWS.home)}>
            See {teamName}’s games
          </Link>
        </div>
      </section>
    );
  }

  const general = feed?.statistics.general ?? null;

  return (
    <section className="fan-panel" aria-label={`Game summary for ${teamName}`}>
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">
          {activeEvent?.name ?? 'Game summary'} — {teamName}
        </h2>
        <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
          Refresh
        </button>
      </div>

      <p className="fanatic__aggregate-note">
        Spectator view: team totals only. Player-by-player numbers stay with the coaching staff.
      </p>

      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}{' '}
          <button className="btn btn--ghost btn--sm" onClick={reload} type="button">
            Try again
          </button>
        </p>
      ) : null}

      {/*
       * AC-FANATIC-005: on a provider that is not fan-visible this renders
       * nothing at all — the slot is absent, not an apology.
       */}
      <div className="fan-video-slot" data-testid="fanatic-summary-video-slot">
        {/* `key` on the tenant re-resolves the embed after a team switch, and
            waiting for `tenantId` keeps the first request from going out
            without the `X-Tenant-ID` the gateway checks the grant against. */}
        {tenantId === null ? null : (
          <VideoEmbed
            eventId={activeEventId}
            isCaptionShown={false}
            isHiddenWhenUnavailable
            key={tenantId}
            title={`${activeEvent?.name ?? 'Game'} video`}
          />
        )}
      </div>

      {isLoading && feed === null ? <p className="fan-empty">Loading team totals…</p> : null}

      {general !== null ? <ScoreByQuarter general={general} teamName={teamName} /> : null}

      {feed !== null ? (
        <ScoringSummary
          drives={feed.statistics.scoring_summary}
          filters={filters}
          isLoading={isLoading}
          onFiltersChange={setFilters}
          opponentName={general?.opponent_name ?? 'Opponent'}
          teamName={teamName}
        />
      ) : null}

      {feed !== null ? (
        <>
          <StatBlock
            isLoading={isLoading}
            isSummaryOnly
            stats={feed.statistics.offense}
            unit="offense"
          />
          <StatBlock
            isLoading={isLoading}
            isSummaryOnly
            stats={feed.statistics.defense}
            unit="defense"
          />
          <StatBlock
            isLoading={isLoading}
            isSummaryOnly
            stats={feed.statistics.special_teams}
            unit="special_teams"
          />
        </>
      ) : null}

      <div className="fan-panel__actions">
        <Link className="btn btn--secondary" to={withTeam(FANATIC_VIEWS.liveVideo)}>
          Live video
        </Link>
        <Link className="btn btn--ghost" to={withTeam(FANATIC_VIEWS.home)}>
          All games
        </Link>
      </div>
    </section>
  );
}
